import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  IconButton,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ReceiptLong as ReceiptLongIcon } from '@mui/icons-material';
import { feeConfigAPI } from '../services/api';

const categoryLabels = {
  consultation: 'Consultation',
  optometry: 'Optometry',
  surgery: 'Surgery',
  investigation: 'Investigation',
  optical: 'Optical',
  pharmacy: 'Pharmacy',
  other: 'Other',
};

const categoryColors = {
  consultation: 'primary',
  optometry: 'info',
  surgery: 'error',
  investigation: 'warning',
  optical: 'secondary',
  pharmacy: 'success',
  other: 'default',
};

const emptyForm = {
  name: '',
  category: 'consultation',
  amount: '',
  hsnSacCode: '',
  gstRate: 0,
  revisitValidityDays: 0,
  revisitFee: 0,
  isActive: true,
};

const FeeMaster = () => {
  const [fees, setFees] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchFees = async () => {
    try {
      const res = await feeConfigAPI.getFeeConfigs();
      setFees(res.data.data);
    } catch (error) {
      showSnackbar('Failed to fetch fee master', 'error');
    }
  };

  useEffect(() => { fetchFees(); }, []);

  const openNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setOpenDialog(true);
  };

  const openEdit = (fee) => {
    setForm({
      name: fee.name,
      category: fee.category,
      amount: fee.amount,
      hsnSacCode: fee.hsnSacCode || '',
      gstRate: fee.gstRate || 0,
      revisitValidityDays: fee.revisitValidityDays || 0,
      revisitFee: fee.revisitFee || 0,
      isActive: fee.isActive,
    });
    setEditingId(fee._id);
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.amount) {
      showSnackbar('Please provide a name and amount', 'error');
      return;
    }
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (editingId) {
        await feeConfigAPI.updateFeeConfig(editingId, payload);
        showSnackbar('Fee item updated');
      } else {
        await feeConfigAPI.createFeeConfig(payload);
        showSnackbar('Fee item created');
      }
      setOpenDialog(false);
      fetchFees();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save fee item', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await feeConfigAPI.deleteFeeConfig(id);
      showSnackbar('Fee item deleted');
      fetchFees();
    } catch (error) {
      showSnackbar('Failed to delete fee item', 'error');
    }
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5, bgcolor: 'rgba(124, 132, 212, 0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main'
          }}>
            <ReceiptLongIcon />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="700">Fee Master</Typography>
            <Typography variant="body2" color="text.secondary">Configure standard fee heads used across check-in &amp; billing</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Add Fee Item</Button>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 'none' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fee Item</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Amount (₹)</TableCell>
                <TableCell align="right">GST %</TableCell>
                <TableCell>Revisit Policy</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fees.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">No fee items configured yet</Typography>
                </TableCell></TableRow>
              ) : fees.map((fee) => (
                <TableRow key={fee._id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{fee.name}</Typography></TableCell>
                  <TableCell>
                    <Chip size="small" label={categoryLabels[fee.category]} color={categoryColors[fee.category]} sx={{ fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="right"><Typography fontWeight={700}>₹{fee.amount.toFixed(2)}</Typography></TableCell>
                  <TableCell align="right">{fee.gstRate || 0}%</TableCell>
                  <TableCell>
                    {fee.revisitValidityDays > 0 ? (
                      <Typography variant="caption">
                        Valid {fee.revisitValidityDays} days, revisit ₹{fee.revisitFee}
                      </Typography>
                    ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={fee.isActive ? 'Active' : 'Inactive'} color={fee.isActive ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(fee)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(fee._id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Fee Item' : 'New Fee Item'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Fee Item Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {Object.entries(categoryLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Amount *" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="HSN/SAC Code" value={form.hsnSacCode} onChange={(e) => setForm({ ...form, hsnSacCode: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="GST Rate (%)" value={form.gstRate} onChange={(e) => setForm({ ...form, gstRate: parseFloat(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Revisit Policy (Indian OPD standard: free revisit within N days)</Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Revisit Validity (days)" value={form.revisitValidityDays} onChange={(e) => setForm({ ...form, revisitValidityDays: parseInt(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Revisit Fee (0 = free)" value={form.revisitFee} onChange={(e) => setForm({ ...form, revisitFee: parseFloat(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{editingId ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FeeMaster;
